import { lazy, Suspense, type ComponentType } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import {
  InvestorLayoutRoute, MemberLayoutRoute, MerchantLayoutRoute, AdminLayoutRoute,
  EmployerLayoutRoute, AffiliatePortalRoute,
} from "@/components/layout/LayoutRoutes";

// Eager: home is the LCP target.
import Index from "./pages/Index";

// Wraps React.lazy with one-shot reload on chunk-load failure. After a deploy,
// the cached index.html points at chunk hashes that no longer exist; rather
// than crashing the whole tree (white screen), reload to fetch the fresh
// index.html and its new asset references. sessionStorage prevents a loop.
function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      const msg = err instanceof Error ? `${err.name} ${err.message}` : String(err);
      const isChunkErr =
        /ChunkLoadError/i.test(msg) ||
        /Loading chunk [\w-]+ failed/i.test(msg) ||
        /Failed to fetch dynamically imported module/i.test(msg);
      if (isChunkErr) {
        const flag = "aamec_reloaded_for_chunk";
        if (!sessionStorage.getItem(flag)) {
          sessionStorage.setItem(flag, "1");
          window.location.reload();
          return new Promise<{ default: T }>(() => { /* never resolves while reloading */ });
        }
      }
      throw err;
    }),
  );
}

// Everything else is code-split — vite emits one chunk per lazy module
// so the homepage no longer ships ~1 MB of dashboard / admin / editor code.
const About = lazyWithReload(() => import("./pages/About"));
const Contact = lazyWithReload(() => import("./pages/Contact"));
const ESG = lazyWithReload(() => import("./pages/ESG"));
const Events = lazyWithReload(() => import("./pages/Events"));
const EventDetail = lazyWithReload(() => import("./pages/EventDetail"));
const BeyondBusinessCards = lazyWithReload(() => import("./pages/BeyondBusinessCards"));
const EventBuy = lazyWithReload(() => import("./pages/EventBuy"));
const Awards = lazyWithReload(() => import("./pages/Awards"));
const AwardNominate = lazyWithReload(() => import("./pages/AwardNominate"));
const AwardNomineeProfile = lazyWithReload(() => import("./pages/AwardNomineeProfile"));
const Marketplace = lazyWithReload(() => import("./pages/Marketplace"));
const SellerDetail = lazyWithReload(() => import("./pages/SellerDetail"));
const ItemDetail = lazyWithReload(() => import("./pages/ItemDetail"));
const Checkout = lazyWithReload(() => import("./pages/Checkout"));
const Partners = lazyWithReload(() => import("./pages/Partners"));
const Chapters = lazyWithReload(() => import("./pages/Chapters"));
const ChapterDetail = lazyWithReload(() => import("./pages/ChapterDetail"));
const Matching = lazyWithReload(() => import("./pages/Matching"));
const DealRoom = lazyWithReload(() => import("./pages/DealRoom"));
const Registry = lazyWithReload(() => import("./pages/Registry"));
const Login = lazyWithReload(() => import("./pages/Login"));
const ForgotPassword = lazyWithReload(() => import("./pages/ForgotPassword"));
const Register = lazyWithReload(() => import("./pages/Register"));
const PaymentCallback = lazyWithReload(() => import("./pages/PaymentCallback"));
const NotFound = lazyWithReload(() => import("./pages/NotFound"));

// Member dashboard
const MemberDashboard = lazyWithReload(() => import("./pages/MemberDashboard"));
const MemberGoods = lazyWithReload(() => import("./pages/MemberGoods"));
const MemberGoodsForm = lazyWithReload(() => import("./pages/MemberGoodsForm"));
const MemberServices = lazyWithReload(() => import("./pages/MemberServices"));
const MemberServicesForm = lazyWithReload(() => import("./pages/MemberServicesForm"));
const MemberEvents = lazyWithReload(() => import("./pages/MemberEvents"));
const MemberEventsForm = lazyWithReload(() => import("./pages/MemberEventsForm"));
const MemberInvestments = lazyWithReload(() => import("./pages/MemberInvestments"));
const DashboardProfile = lazyWithReload(() => import("./pages/DashboardProfile"));
const DashboardSettings = lazyWithReload(() => import("./pages/DashboardSettings"));
const EntrepreneurDashboard = lazyWithReload(() => import("./pages/EntrepreneurDashboard"));
const InvestorDashboard = lazyWithReload(() => import("./pages/InvestorDashboard"));
const Leads = lazyWithReload(() => import("./pages/Leads"));
const Sales = lazyWithReload(() => import("./pages/Sales"));
const Promotions = lazyWithReload(() => import("./pages/Promotions"));

// Merchant
const MerchantRegister = lazyWithReload(() => import("./pages/MerchantRegister"));
const MerchantDashboard = lazyWithReload(() => import("./pages/MerchantDashboard"));
const MerchantOrders = lazyWithReload(() => import("./pages/MerchantOrders"));
const MerchantAnalytics = lazyWithReload(() => import("./pages/MerchantAnalytics"));
const MerchantInvestments = lazyWithReload(() => import("./pages/MerchantInvestments"));

// Admin
const Admin = lazyWithReload(() => import("./pages/Admin"));
const AdminAddMember = lazyWithReload(() => import("./pages/AdminAddMember"));
const AdminAddMerchant = lazyWithReload(() => import("./pages/AdminAddMerchant"));
const AdminMerchantPackageForm = lazyWithReload(() => import("./pages/AdminMerchantPackageForm"));
const AdminAds = lazyWithReload(() => import("./pages/AdminAds"));
const AdminInvestors = lazyWithReload(() => import("./pages/AdminInvestors"));
const AdminInvestorForm = lazyWithReload(() => import("./pages/AdminInvestorForm"));
const AdminOpportunities = lazyWithReload(() => import("./pages/AdminOpportunities"));
const AdminListings = lazyWithReload(() => import("./pages/AdminListings"));
const AdminDealRooms = lazyWithReload(() => import("./pages/AdminDealRooms"));
const AdminInterests = lazyWithReload(() => import("./pages/AdminInterests"));
const AdminEsgInquiries = lazyWithReload(() => import("./pages/AdminEsgInquiries"));
const Inquiries = lazyWithReload(() => import("./pages/Inquiries"));
const Payments = lazyWithReload(() => import("./pages/Payments"));
const AdminPaymentLogs = lazyWithReload(() => import("./pages/AdminPaymentLogs"));
const AdminAddEmployer = lazyWithReload(() => import("./pages/AdminAddEmployer"));

// Jobs & Opportunities (public + employer portal)
const Jobs = lazyWithReload(() => import("./pages/Jobs"));
const JobDetail = lazyWithReload(() => import("./pages/JobDetail"));
const EmployerProfile = lazyWithReload(() => import("./pages/EmployerProfile"));
const EmployerDashboard = lazyWithReload(() => import("./pages/EmployerDashboard"));
const EmployerJobs = lazyWithReload(() => import("./pages/EmployerJobs"));
const EmployerJobForm = lazyWithReload(() => import("./pages/EmployerJobForm"));
const EmployerApplicants = lazyWithReload(() => import("./pages/EmployerApplicants"));
const EmployerProfileSettings = lazyWithReload(() => import("./pages/EmployerProfileSettings"));

// Crowdfunding (public + fundraiser + admin)
const Crowdfunding = lazyWithReload(() => import("./pages/Crowdfunding"));
const CrowdfundingDetail = lazyWithReload(() => import("./pages/CrowdfundingDetail"));
const CrowdfundingDonate = lazyWithReload(() => import("./pages/CrowdfundingDonate"));
const FundraiserDashboard = lazyWithReload(() => import("./pages/FundraiserDashboard"));
const FundraiserCampaignEdit = lazyWithReload(() => import("./pages/FundraiserCampaignEdit"));
const FundraiserDonations = lazyWithReload(() => import("./pages/FundraiserDonations"));
const AdminAddCampaign = lazyWithReload(() => import("./pages/AdminAddCampaign"));
const AdminCrowdfunders = lazyWithReload(() => import("./pages/AdminCrowdfunders"));
const AdminCrowdfundingTransactions = lazyWithReload(() => import("./pages/AdminCrowdfundingTransactions"));
const AdminCrowdfundingCampaigns = lazyWithReload(() => import("./pages/AdminCrowdfundingCampaigns"));
const AdminEmployers = lazyWithReload(() => import("./pages/AdminEmployers"));
const AdminAllJobs = lazyWithReload(() => import("./pages/AdminAllJobs"));
const AdminAllApplications = lazyWithReload(() => import("./pages/AdminAllApplications"));
const AdminMemberAccess = lazyWithReload(() => import("./pages/AdminMemberAccess"));
const AdminFundraiseRequests = lazyWithReload(() => import("./pages/AdminFundraiseRequests"));

// Awards & voting (admin)
const AdminAwards = lazyWithReload(() => import("./pages/AdminAwards"));
const AdminAwardNominations = lazyWithReload(() => import("./pages/AdminAwardNominations"));
const AdminAwardVotes = lazyWithReload(() => import("./pages/AdminAwardVotes"));
const AdminAwardPaymentLogs = lazyWithReload(() => import("./pages/AdminAwardPaymentLogs"));
const AdminAwardContestants = lazyWithReload(() => import("./pages/AdminAwardContestants"));

// Affiliates (affiliate portal + admin)
const AffiliateDashboard = lazyWithReload(() => import("./pages/AffiliateDashboard"));
const AffiliateRegistrations = lazyWithReload(() => import("./pages/AffiliateRegistrations"));
const AffiliateLink = lazyWithReload(() => import("./pages/AffiliateLink"));
const AdminAffiliates = lazyWithReload(() => import("./pages/AdminAffiliates"));
const AdminAffiliateRegistrations = lazyWithReload(() => import("./pages/AdminAffiliateRegistrations"));

// Investor portal
const StartupListingForm = lazyWithReload(() => import("./pages/StartupListingForm"));
const MerchantUpgrade = lazyWithReload(() => import("./pages/MerchantUpgrade"));
const MerchantVerification = lazyWithReload(() => import("./pages/MerchantVerification"));
const AdminMemberInterests = lazyWithReload(() => import("./pages/AdminMemberInterests"));
const AdminVerifications = lazyWithReload(() => import("./pages/AdminVerifications"));
const InvestorPortalDashboard = lazyWithReload(() => import("./pages/InvestorPortalDashboard"));
const InvestorBrowseStartups = lazyWithReload(() => import("./pages/InvestorBrowseStartups"));
const InvestorPipeline = lazyWithReload(() => import("./pages/InvestorPipeline"));
const InvestorProfileSettings = lazyWithReload(() => import("./pages/InvestorProfile"));
const InvestorDeals = lazyWithReload(() => import("./pages/InvestorDeals"));

const queryClient = new QueryClient();

function PageFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="h-8 w-8 animate-spin text-gold" />
    </div>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <RouteErrorBoundary>
            <Suspense fallback={<PageFallback />}>
              <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/about" element={<About />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/chapters" element={<Chapters />} />
              <Route path="/chapters/:slug" element={<ChapterDetail />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/esg" element={<ESG />} />
              <Route path="/events" element={<Events />} />
              <Route path="/beyond-business-cards" element={<BeyondBusinessCards />} />
              <Route path="/events/:slug" element={<EventDetail />} />
              <Route path="/events/:slug/buy" element={<EventBuy />} />
              <Route path="/awards" element={<Awards />} />
              <Route path="/awards/nominate" element={<AwardNominate />} />
              <Route path="/awards/nominee/:slug" element={<AwardNomineeProfile />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/jobs/:slug" element={<JobDetail />} />
              <Route path="/employers/:slug" element={<EmployerProfile />} />
              <Route path="/crowdfunding" element={<Crowdfunding />} />
              <Route path="/crowdfunding/:slug" element={<CrowdfundingDetail />} />
              <Route path="/crowdfunding/:slug/donate" element={<CrowdfundingDonate />} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/marketplace/products" element={<Marketplace mode="products" />} />
              <Route path="/marketplace/services" element={<Marketplace mode="services" />} />
              <Route path="/marketplace/seller/:key" element={<SellerDetail />} />
              <Route path="/marketplace/provider/:key" element={<SellerDetail />} />
              <Route path="/marketplace/vendor/:key" element={<SellerDetail />} />
              <Route path="/marketplace/product/:slug" element={<ItemDetail mode="product" />} />
              <Route path="/marketplace/service/:slug" element={<ItemDetail mode="service" />} />
              <Route path="/checkout/:kind/:slug" element={<Checkout />} />
              <Route path="/matching" element={<Matching />} />
              <Route path="/deal-room" element={<DealRoom />} />
              <Route path="/registry" element={<Registry />} />
              <Route path="/investments" element={<Registry />} />
              {/* Member dashboard — single mounted layout, pages swap via Outlet */}
              <Route element={<MemberLayoutRoute />}>
                <Route path="/dashboard" element={<MemberDashboard />} />
                <Route path="/dashboard/goods" element={<MemberGoods />} />
                <Route path="/dashboard/goods/new" element={<MemberGoodsForm />} />
                <Route path="/dashboard/goods/:id/edit" element={<MemberGoodsForm />} />
                <Route path="/dashboard/services" element={<MemberServices />} />
                <Route path="/dashboard/services/new" element={<MemberServicesForm />} />
                <Route path="/dashboard/services/:id/edit" element={<MemberServicesForm />} />
                <Route path="/dashboard/events" element={<MemberEvents />} />
                <Route path="/dashboard/events/new" element={<MemberEventsForm />} />
                <Route path="/dashboard/events/:id/edit" element={<MemberEventsForm />} />
                <Route path="/dashboard/profile" element={<DashboardProfile />} />
                <Route path="/dashboard/settings" element={<DashboardSettings />} />
                <Route path="/dashboard/entrepreneur" element={<EntrepreneurDashboard />} />
                <Route path="/dashboard/investor" element={<InvestorDashboard />} />
                <Route path="/dashboard/leads" element={<Leads />} />
                <Route path="/dashboard/sales" element={<Sales />} />
                <Route path="/dashboard/promotions" element={<Promotions />} />
                <Route path="/dashboard/investments" element={<MemberInvestments />} />
                <Route path="/dashboard/listings/new" element={<StartupListingForm />} />
                <Route path="/dashboard/listings/:id/edit" element={<StartupListingForm />} />
                <Route path="/dashboard/jobs" element={<EmployerJobs />} />
                <Route path="/dashboard/jobs/new" element={<EmployerJobForm />} />
                <Route path="/dashboard/jobs/:id/edit" element={<EmployerJobForm />} />
                <Route path="/dashboard/applicants" element={<EmployerApplicants />} />
              </Route>
              <Route path="/profile" element={<Navigate to="/dashboard/profile" replace />} />

              {/* Merchant dashboard */}
              <Route element={<MerchantLayoutRoute />}>
                <Route path="/merchant/dashboard" element={<MerchantDashboard />} />
                <Route path="/merchant/leads" element={<Leads />} />
                <Route path="/merchant/sales" element={<Sales />} />
                <Route path="/merchant/promotions" element={<Promotions />} />
                <Route path="/merchant/investments" element={<MerchantInvestments />} />
                <Route path="/merchant/listings/new" element={<StartupListingForm />} />
                <Route path="/merchant/listings/:id/edit" element={<StartupListingForm />} />
                <Route path="/merchant/upgrade" element={<MerchantUpgrade />} />
                <Route path="/merchant/verification" element={<MerchantVerification />} />
                <Route path="/merchant/products" element={<MemberGoods />} />
                <Route path="/merchant/products/new" element={<MemberGoodsForm />} />
                <Route path="/merchant/products/:id/edit" element={<MemberGoodsForm />} />
                <Route path="/merchant/services" element={<MemberServices />} />
                <Route path="/merchant/services/new" element={<MemberServicesForm />} />
                <Route path="/merchant/services/:id/edit" element={<MemberServicesForm />} />
                <Route path="/merchant/events" element={<MemberEvents />} />
                <Route path="/merchant/events/new" element={<MemberEventsForm />} />
                <Route path="/merchant/events/:id/edit" element={<MemberEventsForm />} />
                <Route path="/merchant/orders" element={<MerchantOrders />} />
                <Route path="/merchant/analytics" element={<MerchantAnalytics />} />
                <Route path="/merchant/profile" element={<DashboardProfile />} />
                <Route path="/merchant/settings" element={<DashboardSettings />} />
              </Route>

              {/* Admin dashboard — Admin.tsx renders its own chrome (sidebar/header)
                  so /admin and /admin/:section sit OUTSIDE the layout-route to
                  avoid double sidebars. The dedicated admin pages still go through
                  AdminLayoutRoute for the persistent-chrome behaviour. */}
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/:section" element={<Admin />} />

              <Route element={<AdminLayoutRoute />}>
                <Route path="/admin/members/new" element={<AdminAddMember />} />
                <Route path="/admin/merchants/new" element={<AdminAddMerchant />} />
                <Route path="/admin/employers/new" element={<AdminAddEmployer />} />
                <Route path="/admin/merchants/packages/new" element={<AdminMerchantPackageForm />} />
                <Route path="/admin/merchants/packages/:id/edit" element={<AdminMerchantPackageForm />} />
                <Route path="/admin/leads" element={<Leads adminMode />} />
                <Route path="/admin/inquiries" element={<Inquiries />} />
                <Route path="/admin/sales" element={<Sales adminMode />} />
                <Route path="/admin/ads" element={<AdminAds />} />
                <Route path="/admin/investors" element={<AdminInvestors />} />
                <Route path="/admin/investors/new" element={<AdminInvestorForm />} />
                <Route path="/admin/investors/:id/edit" element={<AdminInvestorForm />} />
                <Route path="/admin/opportunities" element={<AdminOpportunities />} />
                <Route path="/admin/listings" element={<AdminListings />} />
                <Route path="/admin/deal-rooms" element={<AdminDealRooms />} />
                <Route path="/admin/interests" element={<AdminInterests />} />
                <Route path="/admin/member-interests" element={<AdminMemberInterests />} />
                <Route path="/admin/verifications" element={<AdminVerifications />} />
                <Route path="/admin/esg-inquiries" element={<AdminEsgInquiries />} />
                <Route path="/admin/payment-logs" element={<AdminPaymentLogs />} />
                <Route path="/admin/promotions" element={<Promotions adminMode />} />
                <Route path="/admin/events/new" element={<MemberEventsForm />} />
                <Route path="/admin/events/:id/edit" element={<MemberEventsForm />} />
                <Route path="/admin/products/new" element={<MemberGoodsForm />} />
                <Route path="/admin/products/:id/edit" element={<MemberGoodsForm />} />
                <Route path="/admin/services/new" element={<MemberServicesForm />} />
                <Route path="/admin/services/:id/edit" element={<MemberServicesForm />} />
                <Route path="/admin/crowdfunding" element={<AdminCrowdfundingCampaigns />} />
                <Route path="/admin/crowdfunding/new" element={<AdminAddCampaign />} />
                <Route path="/admin/crowdfunders" element={<AdminCrowdfunders />} />
                <Route path="/admin/crowdfunding-transactions" element={<AdminCrowdfundingTransactions />} />
                <Route path="/admin/employers" element={<AdminEmployers />} />
                <Route path="/admin/all-jobs" element={<AdminAllJobs />} />
                <Route path="/admin/all-applications" element={<AdminAllApplications />} />
                <Route path="/admin/member-access" element={<AdminMemberAccess />} />
                <Route path="/admin/fundraise-requests" element={<AdminFundraiseRequests />} />
                <Route path="/admin/awards" element={<AdminAwards />} />
                <Route path="/admin/award-nominations" element={<AdminAwardNominations />} />
                <Route path="/admin/award-votes" element={<AdminAwardVotes />} />
                <Route path="/admin/award-payment-logs" element={<AdminAwardPaymentLogs />} />
                <Route path="/admin/award-contestants" element={<AdminAwardContestants />} />
                <Route path="/admin/affiliates" element={<AdminAffiliates />} />
                <Route path="/admin/affiliate-registrations" element={<AdminAffiliateRegistrations />} />
              </Route>

              {/* Fundraiser portal */}
              <Route element={<MemberLayoutRoute />}>
                <Route path="/fundraiser/dashboard" element={<FundraiserDashboard />} />
                <Route path="/fundraiser/campaigns/:id" element={<FundraiserCampaignEdit />} />
                <Route path="/fundraiser/donations" element={<FundraiserDonations />} />
              </Route>

              {/* Affiliate portal — role-aware chrome: members/vendors keep their
                  own dashboard sidebar; standalone affiliates get a dedicated
                  Affiliate dashboard. */}
              <Route element={<AffiliatePortalRoute />}>
                <Route path="/affiliate" element={<AffiliateDashboard />} />
                <Route path="/affiliate/registrations" element={<AffiliateRegistrations />} />
                <Route path="/affiliate/link" element={<AffiliateLink />} />
              </Route>

              {/* Employer Portal */}
              <Route element={<EmployerLayoutRoute />}>
                <Route path="/employer/dashboard" element={<EmployerDashboard />} />
                <Route path="/employer/jobs" element={<EmployerJobs />} />
                <Route path="/employer/jobs/new" element={<EmployerJobForm />} />
                <Route path="/employer/jobs/:id/edit" element={<EmployerJobForm />} />
                <Route path="/employer/applicants" element={<EmployerApplicants />} />
                <Route path="/employer/profile" element={<EmployerProfileSettings />} />
                <Route path="/employer/settings" element={<EmployerProfileSettings />} />
              </Route>

              {/* Investor dashboard */}
              <Route element={<InvestorLayoutRoute />}>
                <Route path="/investor" element={<InvestorPortalDashboard />} />
                <Route path="/investor/browse" element={<InvestorBrowseStartups />} />
                <Route path="/investor/interested" element={<InvestorPipeline mode="interested-only" />} />
                <Route path="/investor/deals" element={<InvestorDeals />} />
                <Route path="/investor/profile" element={<InvestorProfileSettings />} />
              </Route>

              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/register" element={<Register />} />
              <Route path="/merchant/register" element={<MerchantRegister />} />
              <Route path="/payments" element={<Payments />} />
              <Route path="/payment/callback" element={<PaymentCallback />} />
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </RouteErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
